import { Controller } from "@hotwired/stimulus";
import { resetOnBfcacheRestore } from "../app/helpers/bfcache";

// Plain-Stimulus port of itinerary_detail_overlay.vue + baggage_selector.vue
// (the step1 "Visa resa" detail overlay). One instance of this form is
// pre-rendered per result card (see _step_1_detail_overlay.html.erb),
// hidden until step1_results_controller.js#showDetail reveals it -- no
// per-click fetch needed, matching the Vue original (every itinerary was
// already in memory).
//
// Submits as a real multipart form body (fetch + FormData(this.element)),
// not a hand-built JSON object -- the checkbox names already use Rails'
// bracket nesting (baggage[uuid][ancillary_id], cfar[uuid][index]), which
// Rack parses into the nested params hash SearchController#check expects
// natively, and FormData automatically omits unchecked checkboxes, so
// params[:baggage] only ever contains what's actually selected.
export default class extends Controller {
  static targets = [
    "uuidField",
    "bagOptionButton",
    "baggagePanel",
    "panelChevron",
    "panelBody",
    "optionGroup",
    "groupCheckbox",
    "realCheckbox",
    "wholePartyPrice",
    "totalPrice",
    "submitButton",
    "submitting",
    "errorModal",
  ];
  static values = { checkUrl: String, basePrice: Number };

  connect() {
    const priceEl = this.totalPriceTarget;
    const match = priceEl.textContent.trim().match(/[^\d\s.,]+$/);
    this.currencySuffix = match ? ` ${match[0]}` : "";
    // Some options can already be pre-selected server-side (option[:selected]
    // in _step_1_baggage_panel.html.erb) -- sync Hela/Ingen's initial
    // checked state to match rather than leaving both unchecked regardless
    // of the real starting selection.
    this.baggagePanelTargets.forEach((panel) => this.syncGroupToggles(panel));
    this.recalcPrice();
    this.teardownBfcache = resetOnBfcacheRestore(() => this.resetSubmitting());
  }

  disconnect() {
    if (this.teardownBfcache) this.teardownBfcache();
  }

  // Back out of the submitting state -- see app/helpers/bfcache.js. Without
  // this, Back from step2 returns to a "Förbereder din bokning..." overlay
  // over a permanently disabled "Boka resa".
  resetSubmitting() {
    this.submitButtonTarget.disabled = false;
    if (this.hasSubmittingTarget) {
      this.submittingTarget.classList.add("hidden");
      this.submittingTarget.classList.remove("flex");
    }
  }

  close() {
    const overlay = this.element.closest('[data-step1-results-target="overlay"]');
    if (overlay) overlay.classList.add("hidden");
    document.body.classList.remove("modal-open");
  }

  togglePanel(event) {
    const panel = event.currentTarget.closest('[data-itinerary-detail-target="baggagePanel"]');
    const body = panel.querySelector('[data-itinerary-detail-target="panelBody"]');
    const chevron = panel.querySelector('[data-itinerary-detail-target="panelChevron"]');
    body.classList.toggle("hidden");
    chevron.classList.toggle("rotate-180");
  }

  toggleOption(event) {
    const panel = event.currentTarget.closest('[data-itinerary-detail-target="baggagePanel"]');
    const group = event.currentTarget.closest('[data-itinerary-detail-target="optionGroup"]');
    const checked = event.currentTarget.checked;
    group.querySelectorAll('[data-itinerary-detail-target="realCheckbox"]').forEach((cb) => {
      cb.checked = checked;
    });
    this.syncGroupToggles(panel);
    this.recalcPrice();
  }

  // "Hela resesällskapet" -- a real toggle, not a one-way "select all"
  // action: reads its OWN new checked state (the native click already
  // flipped it before this handler runs), so checking it selects every
  // passenger and unchecking it deselects every passenger. syncGroupToggles
  // then re-derives both this and "Ingen"'s checked state from the actual
  // passenger checkboxes afterward, so this stays correct even though we
  // just forced it -- e.g. unchecking "Hela" here also flips "Ingen" on,
  // since zero passengers ends up selected, which is the correct reflection
  // of reality rather than a second hardcoded rule.
  toggleAllInPanel(event) {
    const panel = event.currentTarget.closest('[data-itinerary-detail-target="baggagePanel"]');
    const checked = event.currentTarget.checked;
    panel.querySelectorAll('[data-itinerary-detail-target="groupCheckbox"]').forEach((cb) => {
      cb.checked = checked;
    });
    panel.querySelectorAll('[data-itinerary-detail-target="realCheckbox"]').forEach((cb) => {
      cb.checked = checked;
    });
    if (checked) {
      const body = panel.querySelector('[data-itinerary-detail-target="panelBody"]');
      body.classList.remove("hidden");
    }
    this.syncGroupToggles(panel);
    this.recalcPrice();
  }

  // "Ingen i resesällskapet" -- checking it deselects every passenger.
  // Unchecking it directly (clicking it a second time) is left as a no-op
  // on the passengers themselves; syncGroupToggles will immediately
  // re-check it anyway as long as the passenger set is still all-unselected
  // (the only way it could have been checked in the first place), so this
  // only really matters once some other change has moved it out of sync.
  toggleNoneInPanel(event) {
    const panel = event.currentTarget.closest('[data-itinerary-detail-target="baggagePanel"]');
    if (event.currentTarget.checked) {
      panel.querySelectorAll('[data-itinerary-detail-target="groupCheckbox"]').forEach((cb) => {
        cb.checked = false;
      });
      panel.querySelectorAll('[data-itinerary-detail-target="realCheckbox"]').forEach((cb) => {
        cb.checked = false;
      });
    }
    this.syncGroupToggles(panel);
    this.recalcPrice();
  }

  // Re-derives "Hela resesällskapet"/"Ingen i resesällskapet"'s own checked
  // state from the actual passenger checkboxes, rather than treating them
  // as independently-set flags -- called after every change that could
  // affect the aggregate (an individual passenger toggling, or the two
  // group checkboxes themselves). Keeps all three checkboxes in this panel
  // permanently consistent with each other regardless of which one the
  // user actually clicked.
  syncGroupToggles(panel) {
    const groupCheckboxes = Array.from(panel.querySelectorAll('[data-itinerary-detail-target="groupCheckbox"]'));
    if (groupCheckboxes.length === 0) return;
    const allChecked = groupCheckboxes.every((cb) => cb.checked);
    const noneChecked = groupCheckboxes.every((cb) => !cb.checked);

    const allCheckbox = panel.querySelector('[data-itinerary-detail-target="allCheckbox"]');
    const noneCheckbox = panel.querySelector('[data-itinerary-detail-target="noneCheckbox"]');
    if (allCheckbox) allCheckbox.checked = allChecked;
    if (noneCheckbox) noneCheckbox.checked = noneChecked;
  }

  // Template 2's "Utan bagage" / "Med incheckat bagage" pair -- selects
  // which itinerary uuid this form submits and switches recalcPrice's own
  // starting total to that option's real price (still adding any other
  // selected checkbox, e.g. climate compensation, on top).
  selectBagOption(event) {
    const btn = event.currentTarget;
    this.uuidFieldTarget.value = btn.dataset.uuid;
    this.basePriceValue = Number(btn.dataset.price) || 0;
    this.bagOptionButtonTargets.forEach((b) => {
      b.classList.toggle("border-primary", b === btn);
      b.classList.toggle("bg-primary-light", b === btn);
      b.classList.toggle("border-gray-200", b !== btn);
    });
    this.recalcPrice();
  }

  recalcPrice() {
    let total = this.basePriceValue;
    this.element.querySelectorAll('input[type="checkbox"][data-price]').forEach((checkbox) => {
      if (checkbox.checked) total += Number(checkbox.dataset.price) || 0;
    });
    this.totalPriceTarget.textContent = `${Math.round(total).toLocaleString("sv-SE")}${this.currencySuffix || ""}`;
  }

  submit(event) {
    event.preventDefault();
    this.submitButtonTarget.disabled = true;
    if (this.hasSubmittingTarget) {
      this.submittingTarget.classList.remove("hidden");
      this.submittingTarget.classList.add("flex");
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch(this.checkUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken || "", Accept: "application/json" },
      body: new FormData(this.element),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.available) {
          window.location.href = data.url;
        } else {
          this.showError();
        }
      })
      .catch(() => this.showError());
  }

  showError() {
    this.resetSubmitting();
    if (this.hasErrorModalTarget) {
      this.errorModalTarget.classList.remove("hidden");
      this.errorModalTarget.classList.add("flex");
    }
  }

  // Wired on the errorModal backdrop itself -- same pattern as every other
  // popup on the site (event.currentTarget is the backdrop, event.target
  // is whatever was actually clicked, equal only when the click didn't
  // bubble up from the card content inside it).
  closeErrorOnBackdrop(event) {
    if (event.target !== event.currentTarget) return;
    this.errorModalTarget.classList.add("hidden");
    this.errorModalTarget.classList.remove("flex");
  }
}
